import type { AudioEngine } from '../../engine/audio/audio-engine';
import { cacheableAudioUrl } from '../../services/audio-cache-url';
import { reportClientError } from '../../services/appwrite/report-client-error';
import { mediaGateway } from '../../sites/role/features/media/media-gateway';

/**
 * El sonido de aviso, del catálogo del sitio (`site_sound_events`).
 *
 * El servidor mantiene el catálogo y entrega una sola vez un manifiesto con
 * todas las URL públicas. `RealtimeSync` es el único que lo usa hoy, para el
 * sonido de notificación: vive acá, junto a su único consumidor, en vez de
 * en una carpeta de una sola aplicación como cuando era parte de la Taberna.
 */

interface SoundManifestItem {
  eventKey: string;
  url: string;
  audioAssetId: string;
  volume: number;
}

interface SoundManifestResponse {
  sounds?: SoundManifestItem[];
}

interface ResolvedSound {
  url: string;
  cacheId: string;
  volume: number;
}

let manifestRequest: Promise<Map<string, ResolvedSound>> | null = null;

function soundManifest(): Promise<Map<string, ResolvedSound>> {
  if (manifestRequest) return manifestRequest;
  manifestRequest = mediaGateway.run<SoundManifestResponse>('get_site_sound_manifest')
    .then((result) => {
      const sounds = new Map<string, ResolvedSound>();
      for (const item of result.sounds ?? []) {
        if (!item.eventKey || !item.url || !item.audioAssetId) continue;
        sounds.set(item.eventKey, {
          url: item.url,
          cacheId: `site-sound:${item.audioAssetId}`,
          volume: typeof item.volume === 'number' ? item.volume : 1,
        });
      }
      return sounds;
    })
    .catch((caught: unknown) => {
      // El sonido nunca bloquea el aviso. Se informa una sola falla por
      // manifiesto, no una por cada clave, para no amplificar un corte de red.
      console.error('[Rol Sensorial] No se pudo cargar el manifiesto de sonidos.', caught);
      reportClientError({
        source: 'web-app',
        operation: 'notification-sound-manifest',
        level: 'warning',
        message: 'No se pudo cargar el manifiesto de sonidos.',
        details: String(caught),
      });
      return new Map<string, ResolvedSound>();
    });
  return manifestRequest;
}

async function soundFor(key: string): Promise<ResolvedSound | null> {
  return (await soundManifest()).get(key) ?? null;
}

/**
 * Reproduce un sonido del catálogo por su clave.
 *
 * Con `cache`, el audio se guarda en el navegador la primera vez y las
 * siguientes suena sin pedir nada. Si no se puede guardar, se reproduce igual
 * desde su dirección: la caché es una mejora, no un requisito.
 */
export async function playSiteSound(
  engine: AudioEngine,
  key: string,
  cache = true,
): Promise<void> {
  const sound = await soundFor(key);
  if (!sound) return;
  const url = cache ? cacheableAudioUrl(sound.cacheId, sound.url) : sound.url;
  engine.register([{ id: key, sources: [url], html5: true, volume: sound.volume }]);
  engine.playEffect(key);
}

/**
 * Resuelve, registra y precarga un sonido antes de que llegue el evento que
 * lo usa. No lo reproduce: solo evita que el primer aviso tenga que esperar
 * una llamada a la Function de medios o la descarga del archivo.
 */
export async function prepareSiteSound(
  engine: AudioEngine,
  key: string,
  cache = true,
): Promise<void> {
  const sound = await soundFor(key);
  if (!sound) return;
  const url = cache ? cacheableAudioUrl(sound.cacheId, sound.url) : sound.url;
  engine.register([{ id: key, sources: [url], html5: true, volume: sound.volume }]);

  // `register` solo deja el sonido listo en el motor. Cuando el service
  // worker ya controla la página, pedir la URL local aquí sí llena la caché
  // persistente y deja la primera reproducción libre de una descarga.
  if (cache && navigator.serviceWorker?.controller) {
    try {
      const response = await fetch(url, { credentials: 'omit' });
      if (!response.ok && response.type !== 'opaque') throw new Error(`HTTP ${response.status}`);
    } catch (caught: unknown) {
      reportClientError({
        source: 'web-app',
        operation: 'preload-notification-sound',
        level: 'warning',
        message: `No se pudo precargar el sonido: ${key}`,
        details: String(caught),
      });
    }
  }
}
